"use client";

import React, { useState, useEffect } from "react";
import { Cpu, Code, Brain, Zap, Shield, Activity, Server, Wifi, Layers, Maximize, Terminal, Lock } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface TechSpec {
  label: string;
  value: string;
}
interface TechItem {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  specs: TechSpec[];
  badge: string;
  image: string;
}
interface TechCategory {
  id: string;
  label: string;
  icon: React.ElementType;
  items: TechItem[];
}
const techCategories: TechCategory[] = [{
  id: "hardware",
  label: "Voice Engine",
  icon: Cpu,
  items: [{
    id: "hw-1",
    title: "Neural Acoustic Processor",
    description: "High-fidelity voice capture system that filters industrial background noise to isolate homeowner distress signals.",
    icon: Layers,
    badge: "MIL-SPEC",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/tech_microled_schematic.png",
    specs: [{
      label: "Processing",
      value: "30ms Lag"
    }, {
      label: "Isolation",
      value: "98.2 dB"
    }, {
      label: "Sampling",
      value: "192kHz"
    }]
  }, {
    id: "hw-2",
    title: "Emergency Triage Unit",
    description: "Autonomous decision-making node that prioritizes burst pipes and HVAC failures over general maintenance inquiries.",
    icon: Server,
    badge: "24/7 Active",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/tech_edge_processor.png",
    specs: [{
      label: "Logic",
      value: "Triage v4"
    }, {
      label: "Capacity",
      value: "500 Calls"
    }, {
      label: "Uptime",
      value: "99.99%"
    }]
  }, {
    id: "hw-3",
    title: "Natural Dialect Synth",
    description: "Advanced voice synthesis that mimics local regional accents to build immediate trust with calling customers.",
    icon: Wifi,
    badge: "Human-Grade",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/tech_sensor_array.png",
    specs: [{
      label: "Latent",
      value: "<150ms"
    }, {
      label: "Dialects",
      value: "All US"
    }, {
      label: "Tone",
      value: "Empathetic"
    }]
  }]
}, {
  id: "software",
  label: "CRM Nexus",
  icon: Code,
  items: [{
    id: "sw-1",
    title: "ServiceTitan Bridge",
    description: "Hard-wired bi-directional sync that populates customer cards and job details directly into your dispatch board.",
    icon: Maximize,
    badge: "API Certified",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/tech_cms_dashboard.png",
    specs: [{
      label: "Sync",
      value: "Real-time"
    }, {
      label: "Security",
      value: "AES-512"
    }, {
      label: "Mapping",
      value: "Auto-Field"
    }]
  }, {
    id: "sw-2",
    title: "Lead Integrity Vault",
    description: "Secure data repository that captures every missed call recording and transcription for quality assurance and training.",
    icon: Activity,
    badge: "Industrial",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/tech_telemetry_graph.png",
    specs: [{
      label: "Storage",
      value: "Unlimited"
    }, {
      label: "Search",
      value: "Semantic"
    }, {
      label: "Export",
      value: "PDF/JSON"
    }]
  }, {
    id: "sw-3",
    title: "Auto-Dispatch Logic",
    description: "Intelligent scheduling algorithm that matches emergency leads with the closest technician based on GPS telemetry.",
    icon: Lock,
    badge: "ISO 27001",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/tech_security_lock.png",
    specs: [{
      label: "Matching",
      value: "Proximity"
    }, {
      label: "Protocol",
      value: "Direct-Push"
    }, {
      label: "Audit",
      value: "Full Trail"
    }]
  }]
}, {
  id: "ai",
  label: "Recovery Logic",
  icon: Brain,
  items: [{
    id: "ai-1",
    title: "Revenue Leak Detection",
    description: "Scans inbound call logs to identify unbooked leads and triggers immediate AI-voice follow-up protocols.",
    icon: Zap,
    badge: "ROI Focused",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/tech_ai_vision_heatmap.png",
    specs: [{
      label: "Detection",
      value: "99.9%"
    }, {
      label: "Reaction",
      value: "<2 Mins"
    }, {
      label: "Recovery",
      value: "High-Ticket"
    }]
  }, {
    id: "ai-2",
    title: "Predictive Booking",
    description: "Analyzes historical agency data to forecast peak call volumes and adjust AI capacity for zero missed leads.",
    icon: Shield,
    badge: "Autonomic",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/tech_predictive_chart.png",
    specs: [{
      label: "Forecast",
      value: "14 Days"
    }, {
      label: "Accuracy",
      value: "94.5%"
    }, {
      label: "Scaling",
      value: "Elastic"
    }]
  }, {
    id: "ai-3",
    title: "ROI Telemetry Engine",
    description: "Real-time dashboard displaying recovered revenue, saved labor costs, and operational efficiency metrics.",
    icon: Terminal,
    badge: "Financial",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1606688/img/tech_dynamic_adjust.png",
    specs: [{
      label: "Update",
      value: "Instant"
    }, {
      label: "Display",
      value: "Industrial"
    }, {
      label: "Precision",
      value: "Cent-Level"
    }]
  }]
}];
function TechCard({
  item,
  isLoading
}: {
  item: TechItem;
  isLoading: boolean;
}) {
  return <Card className="bg-card/30 backdrop-blur-sm border-border/40 rounded-none overflow-hidden group hover:border-primary/60 transition-all duration-300 h-full flex flex-col shadow-lg shadow-black/20">
      <div className="relative h-48 w-full overflow-hidden bg-muted/10 border-b border-border/40">
        {isLoading ? <Skeleton className="h-full w-full rounded-none bg-accent/5" /> : <>
            <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent z-10 opacity-70" />
            <img src={item.image} alt={item.title} className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-1000 grayscale group-hover:grayscale-0" />
            <div className="absolute top-3 right-3 z-20">
              <Badge variant="outline" className="bg-background/90 backdrop-blur-none text-[10px] font-mono border-primary/40 text-primary rounded-none px-2 py-0.5 uppercase tracking-tighter">
                {item.badge}
              </Badge>
            </div>
            {}
            <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10 mix-blend-overlay pointer-events-none" />
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#B5A6420a_1px,transparent_1px),linear-gradient(to_bottom,#B5A6420a_1px,transparent_1px)] bg-[size:16px_16px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
          </>}
      </div>

      <CardHeader className="pb-2">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 rounded-none bg-primary/10 text-primary border border-primary/20">
            <item.icon className="size-4" />
          </div>
          <CardTitle className="text-xl font-serif font-bold uppercase tracking-tight text-foreground">{item.title}</CardTitle>
        </div>
        <CardDescription className="text-sm leading-relaxed line-clamp-3 font-sans text-muted-foreground">
          {item.description}
        </CardDescription>
      </CardHeader>

      <CardContent className="mt-auto pt-4">
        {isLoading ? <div className="space-y-2">
            <Skeleton className="h-4 w-full bg-accent/5" />
            <Skeleton className="h-4 w-2/3 bg-accent/5" />
          </div> : <div className="grid grid-cols-3 gap-2">
            {item.specs.map((spec, idx) => <div key={idx} className="flex flex-col gap-1 p-2 rounded-none bg-muted/20 border border-border/20">
                <span className="text-[9px] uppercase text-primary font-mono tracking-widest font-bold">{spec.label}</span>
                <span className="text-xs font-medium text-foreground font-mono">{spec.value}</span>
              </div>)}
          </div>}
      </CardContent>
    </Card>;
}
export default function TheTechStack() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [activeTab, setActiveTab] = useState("hardware");
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 600);
    return () => clearTimeout(timer);
  }, [activeTab]);
  return <section data-section-id="38552" id="the-infrastructure-specs" ref={ref} className={`dark bg-background text-foreground py-24 relative overflow-hidden transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      {}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-secondary/10 rounded-full blur-[128px] pointer-events-none" />
      
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#B5A64205_1px,transparent_1px),linear-gradient(to_bottom,#B5A64205_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="flex flex-col items-center text-center mb-16 max-w-3xl mx-auto">
          <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/5 px-4 py-1 rounded-none uppercase tracking-widest text-[10px] font-mono font-bold">
            Operational Blueprint
          </Badge>
          <h2 className="text-4xl md:text-6xl font-serif font-bold tracking-tighter mb-6 bg-clip-text text-transparent bg-gradient-to-b from-foreground to-foreground/50 uppercase">
            The Infrastructure Specs
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-sans font-light">
            A high-performance ecosystem of industrial-grade AI voice intelligence, secure CRM bridges, and revenue recovery logic built for elite home service agencies.
          </p>
        </div>

        <Tabs defaultValue="hardware" value={activeTab} onValueChange={setActiveTab} className="w-full max-w-6xl mx-auto">
          <div className="flex justify-center mb-12">
            <TabsList className="bg-muted/10 border border-border/20 p-1 h-auto gap-1 backdrop-blur-sm rounded-none">
              {techCategories.map((category, i) => <TabsTrigger key={category.id} value={category.id} data-index={i} className="rounded-none px-8 py-3 text-xs uppercase font-mono tracking-widest data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-200">
                  <category.icon className="w-4 h-4 mr-2" />
                  {category.label}
                </TabsTrigger>)}
            </TabsList>
          </div>

          {techCategories.map((category, i) => <TabsContent key={category.id} value={category.id} className="mt-0 outline-none" data-index={i}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {category.items.map((item, idx) => <div key={item.id} data-index={idx} className="h-full">
                    <TechCard item={item} isLoading={isLoading} />
                  </div>)}
              </div>
            </TabsContent>)}
        </Tabs>

        <div className="mt-20 flex justify-center">
          <Link to="/TheEnginePage">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none px-10 py-7 text-sm font-mono font-bold uppercase tracking-widest shadow-[6px_6px_0px_0px_rgba(0,0,0,0.3)] hover:translate-y-0.5 active:translate-y-1 transition-all">
              <Terminal className="mr-2 h-5 w-5" />
              Access Engine Documentation
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}